import { Input } from "antd";
import { useState, useEffect, useMemo } from "react";
import "./GalleryList.scss";
import { ProjectCard } from "../ProjectCard/ProjectCard";
import { IProjectInfo } from "../ProjectsGallery";
import { fetchUserAttributes } from "aws-amplify/auth";
import portalScreenshot from "../../../assets/portal-screenshot.png";
import { SearchIcon } from "../../../icons-and-animations/Icons";
import { HELP_CENTER_URL } from "../../../stores/consts";

export interface GalleryListProps {
  recentProjects: IProjectInfo[];
  isLoadingProjects: boolean;
}

export default function GalleryList(props: GalleryListProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [email, setEmail] = useState<string | undefined>("");
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const isSearching = normalizedSearchTerm.length > 0;
  const loadedProjectCount = props.recentProjects.length;

  const filteredProjects = useMemo(() => {
    if (!normalizedSearchTerm) {
      return props.recentProjects;
    }

    return props.recentProjects.filter((project) =>
      project.title.toLowerCase().includes(normalizedSearchTerm)
    );
  }, [normalizedSearchTerm, props.recentProjects]);

  const loadedProjectLabel = `${loadedProjectCount} ${
    loadedProjectCount === 1 ? "project" : "projects"
  } loaded`;

  let headerDescription = "Click on a project to edit";
  if (props.isLoadingProjects) {
    headerDescription = `${loadedProjectLabel}. More projects are still loading.`;
    if (isSearching) {
      headerDescription = `Searching ${loadedProjectLabel}. More projects are still loading.`;
    }
  }

  const emptySearchText = props.isLoadingProjects
    ? "No matches in the loaded projects yet. More projects are still loading."
    : "No projects match your search.";
  const searchPlaceholder = props.isLoadingProjects
    ? "Search loaded projects"
    : "Search projects";

  useEffect(() => {
    fetchUserAttributes().then((attributes) => {
      setEmail(attributes?.email);
    });
  }, []);

  return (
    <div className={"gallery-list-container"}>
      <div className="top-bar">
        <div className="top-bar-info-item">
          <div className="level-1">Welcome to the studio</div>
          <div className="level-2">{email}</div>
        </div>
        <div className="extra-info">
          <div
            className="custom-card"
            onClick={() => window.open(HELP_CENTER_URL, "_blank")}
          >
            <div className="card-body">
              <div className="card-meta-title">
                Explore Blings: Help Center & Knowledge Base
              </div>
              <div className="card-meta-description">Learn more</div>
            </div>
            <div className="image-container">
              <img
                src={portalScreenshot}
                alt={"Portal Screenshot"}
                style={{ width: "100%", height: "auto", objectFit: "cover" }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={"gallery-list-header"}>
        <div className="gallery-list-header-text">
          <div className="level-1">Recent Projects</div>
          <div
            className={`level-2 ${
              props.isLoadingProjects ? "gallery-list-loading-copy" : ""
            }`}
            role={props.isLoadingProjects ? "status" : undefined}
            aria-live={props.isLoadingProjects ? "polite" : undefined}
          >
            {headerDescription}
          </div>
        </div>
        <div className="gallery-list-actions">
          <Input
            size="large"
            variant="borderless"
            className="project-search-input"
            placeholder={searchPlaceholder}
            suffix={<SearchIcon />}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
          />
        </div>
      </div>
      {filteredProjects.length > 0 ? (
        <div className="gallery-project-grid">
          {filteredProjects.map((project) => (
            <ProjectCard
              title={project.title}
              thumbnail={project.thumbnail}
              updatedAt={project.updatedAt}
              id={project.id}
              key={project.key}
            />
          ))}
        </div>
      ) : (
        <div className="gallery-list-empty-state" role="status">
          {isSearching ? emptySearchText : "No projects available."}
        </div>
      )}
    </div>
  );
}
